import { Chip } from '@mui/material';
import { concat, find, transform } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CopyTextId,
  CrudPage,
  CrudTableActionType,
  DataGridColumnDefinition,
  DateLabel,
  FormikSelectOption,
  Money,
} from 'components';
import { AppConfigArea, AppConfigKey, FilterDefinitionType } from 'enums';
import {
  EditInternalTransferDialog,
  InternalTransferStatusLabel,
} from 'features/internal-transfers';
import {
  useAppConfigValue,
  useQueryFilters,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  InternalTransfer,
  InternalTransferFilters,
  PaginatedData,
  Shop,
} from 'types';
import { filtersUtils, formatUtils, userUtils } from 'utils';

type Props = {
  archived?: boolean;
  queryResult: UseQueryResult<PaginatedData<InternalTransfer>>;
  queryResultShops?: UseQueryResult<Shop[]>;
  hideTitle?: boolean;
  additionalColumns?: DataGridColumnDefinition<InternalTransfer>[];
  additionalFilters?: FilterDefinition<Partial<InternalTransferFilters>>[];
};

export const InternalTransfers: React.FC<Props> = ({
  archived,
  queryResult,
  queryResultShops,
  additionalColumns = [],
  additionalFilters = [],
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.internal_transfers',
  });
  const { filters } = useQueryFilters<InternalTransferFilters>();
  const { p2pProviders } = useUserContext();
  const { isAdmin, isAdminOrTechOperator } = useUser();

  const categoriesConfigQueryResult = useAppConfigValue(
    {
      area: AppConfigArea.Platform,
      key: AppConfigKey.InternalTransferCategories,
    },
    { enabled: isAdminOrTechOperator },
  );

  const categoryOptions = useMemo(
    () =>
      transform(
        categoriesConfigQueryResult?.data || {},
        (acc, label, value) => acc.push({ label: label as string, value }),
        [] as FormikSelectOption[],
      ),
    [categoriesConfigQueryResult?.data],
  );

  const getCategoryLabel = useCallback(
    (category: string) =>
      (find(categoryOptions, { value: category })?.label as string) || category,
    [categoryOptions],
  );

  const commonFilters: FilterDefinition<InternalTransferFilters>[] = useMemo(
    () => [
      {
        label: t('fields.category'),
        name: 'category',
        type: FilterDefinitionType.Select,
        getDisplayName: getCategoryLabel,
        options: categoryOptions,
        hidden: !isAdminOrTechOperator,
      },
      {
        label: t('fields.code'),
        name: 'id',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
    ],
    [t, isAdminOrTechOperator, categoryOptions, getCategoryLabel],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<InternalTransfer>[] => [
      {
        header: t('fields.code'),
        valueGetter: (item) => <CopyTextId id={item.id} />,
      },
      {
        header: t('fields.date'),
        valueGetter: (item) => (
          <DateLabel>{formatUtils.formatDate(item.createdAt)}</DateLabel>
        ),
      },
      {
        header: t('fields.amount'),
        valueGetter: (item) => (
          <Money
            value={item.amount}
            assetCurrencyId={item.senderAsset?.assetCurrencyId}
            symbol
          />
        ),
      },
      {
        header: t('fields.details'),
        valueGetter: (item) => {
          if (!item.details) {
            return '-';
          }
          return t(`details.${item.details}`);
        },
      },
      {
        header: t('fields.status'),
        valueGetter: (item) => (
          <InternalTransferStatusLabel internalTransfer={item} />
        ),
      },
      {
        header: t('fields.sender'),
        valueGetter: (item) => {
          const label = userUtils.getAnonymizedUserLabel(item.senderUser);
          const shopName = find(queryResultShops?.data, {
            id: item.senderAsset?.shopId,
          })?.name;
          const p2pProviderName = item.senderAsset?.p2pProviderId
            ? find(p2pProviders, { id: item.senderAsset?.p2pProviderId })?.name
            : null;

          return (
            <Fragment>
              <div>{label}</div>
              {shopName && <div>{shopName}</div>}
              {p2pProviderName && <div>{p2pProviderName}</div>}
            </Fragment>
          );
        },
      },
      {
        header: t('fields.category'),
        valueClassName: 'tw-min-w-[200px]',
        valueGetter: (item) => (
          <div>
            {item.category && (
              <Chip
                label={getCategoryLabel(item.category)}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
            {item.comment && (
              <div className="tw-text-secondary tw-text-gray-500 tw-italic tw-p-1">
                {item.comment}
              </div>
            )}
          </div>
        ),
        hidden: !isAdminOrTechOperator,
      },
      ...(additionalColumns ? additionalColumns : []),
    ],
    [
      t,
      additionalColumns,
      queryResultShops,
      p2pProviders,
      isAdminOrTechOperator,
      getCategoryLabel,
    ],
  );

  const filtersDefinitions = useMemo(
    () =>
      concat(
        filtersUtils.getCommonFilters(filters),
        commonFilters,
        additionalFilters,
      ),
    [additionalFilters, commonFilters, filters],
  );

  return (
    <CrudPage
      filters={{ filtersDefinitions }}
      table={{
        queryResult,
        columns,
        paginated: true,
        actions:
          isAdmin && archived
            ? [
                {
                  type: CrudTableActionType.Details,
                  renderDialog: EditInternalTransferDialog,
                },
              ]
            : undefined,
      }}
    />
  );
};
